import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ShipmentDetails from '../components/ShipmentDetails';
import './DeliveryNoteCreation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getPalletInfo = (item, palletNumber) =>
  item.inventory?.pallets?.find((p) => p.pallet_number === palletNumber);

const getAvailablePalletNumbers = (item) =>
  (item.inventory?.pallets || [])
    .filter((p) => p.status === 'available')
    .map((p) => p.pallet_number);

export default function DeliveryNoteCreation({ token }) {
  const navigate = useNavigate();

  const [shipments, setShipments] = useState([]);
  const [cargoItems, setCargoItems] = useState([]);
  const [shipmentData, setShipmentData] = useState(null);
  const [packingList, setPackingList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPallets, setSelectedPallets] = useState({});

  const [formData, setFormData] = useState({
    shipment_id: '',
    customer_name: '',
    delivery_address: '',
    delivery_city: '',
    delivery_country: '',
    truck_model: '',
    truck_registration: '',
    driver_name: '',
    driver_phone: '',
    notes: ''
  });

  useEffect(() => {
    if (token) {
      fetchShipments();
    }
  }, [token]);

  const fetchShipments = async () => {
    try {
      const response = await fetch(`${API_URL}/packing-lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load shipments');

      const data = await response.json();
      setShipments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error loading shipments: ' + err.message);
    }
  };

  const handleShipmentChange = async (e) => {
    const packing_list_id = e.target.value;
    setFormData(prev => ({
      ...prev,
      shipment_id: packing_list_id
    }));
    setSelectedPallets({});
    setShipmentData(null);

    if (packing_list_id) {
      try {
        const response = await fetch(`${API_URL}/packing-lists/${packing_list_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to load cargo items');

        const data = await response.json();
        setCargoItems(data.cargo_items || []);
        setPackingList(data);  // Store the full packing list

        // Fetch shipment details to auto-populate
        const shipmentResponse = await fetch(`${API_URL}/packing-lists/${packing_list_id}/shipment`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (shipmentResponse.ok) {
          const shipmentInfo = await shipmentResponse.json();
          setShipmentData(shipmentInfo);
          
          // Auto-populate from shipment data
          setFormData(prev => ({
            ...prev,
            customer_name: shipmentInfo.client_name || '',
            delivery_address: shipmentInfo.delivery_address || '',
            delivery_city: shipmentInfo.delivery_city || '',
            delivery_country: shipmentInfo.delivery_country || ''
          }));
        }
      } catch (err) {
        setError('Error loading cargo items: ' + err.message);
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePalletToggle = (cargoId, palletNumber, item) => {
    const palletInfo = getPalletInfo(item, palletNumber);
    if (palletInfo && palletInfo.status !== 'available') {
      return;
    }

    setSelectedPallets(prev => {
      const cargoKey = `cargo-${cargoId}`;
      const currentPallets = prev[cargoKey] || [];
      
      if (currentPallets.includes(palletNumber)) {
        return {
          ...prev,
          [cargoKey]: currentPallets.filter(p => p !== palletNumber)
        };
      } else {
        return {
          ...prev,
          [cargoKey]: [...currentPallets, palletNumber]
        };
      }
    });
  };

  const handleSelectAllPallets = (item) => {
    const cargoKey = `cargo-${item.cargo_id}`;
    const availablePallets = getAvailablePalletNumbers(item);
    const currentPallets = selectedPallets[cargoKey] || [];

    if (currentPallets.length === availablePallets.length && availablePallets.length > 0) {
      setSelectedPallets(prev => ({
        ...prev,
        [cargoKey]: []
      }));
    } else {
      setSelectedPallets(prev => ({
        ...prev,
        [cargoKey]: availablePallets
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!formData.shipment_id) {
        setError('Please select a shipment');
        setLoading(false);
        return;
      }

      if (!formData.customer_name) {
        setError('Customer name is required');
        setLoading(false);
        return;
      }

      if (!formData.delivery_address) {
        setError('Delivery address is required');
        setLoading(false);
        return;
      }

      if (!formData.driver_name) {
        setError('Driver name is required');
        setLoading(false);
        return;
      }

      const items = [];
      let sequenceNumber = 1;
      
      Object.entries(selectedPallets).forEach(([cargoKey, pallets]) => {
        const cargoId = parseInt(cargoKey.split('-')[1]);
        const cargo = cargoItems.find(c => c.cargo_id === cargoId);
        
        if (cargo && pallets.length > 0) {
          pallets.forEach(palletNumber => {
            const palletInfo = getPalletInfo(cargo, palletNumber);
            items.push({
              cargo_id: cargoId,
              pallet_number: palletNumber,
              quantity_delivered: palletInfo?.quantity ?? cargo.quantity_pieces,
              sequence_number: sequenceNumber++
            });
          });
        }
      });

      if (items.length === 0) {
        setError('Please select at least one pallet to deliver');
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        shipment_id: parseInt(formData.shipment_id),
        items
      };

      const response = await fetch(`${API_URL}/delivery-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to create delivery note');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setSuccess(`Delivery note ${data.delivery_number} created successfully!`);

      setTimeout(() => {
        navigate('/delivery-notes');
      }, 1500);
    } catch (err) {
      setError('Error creating delivery note: ' + err.message);
      setLoading(false);
    }
  };

  return (
    <div className="delivery-note-creation-container">
      <div className="creation-header">
        <h1>Create Delivery Note</h1>
        <p>Select pallets from a shipment and record delivery details</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Shipment Details Block */}
      {shipmentData && (
        <ShipmentDetails 
          shipment={shipmentData} 
          packing_list={packingList}
          loading={false}
        />
      )}

      <form onSubmit={handleSubmit} className="creation-form">
        
        {/* LEFT COLUMN: Shipment & Pallet Selection */}
        <div className="form-column">
          <fieldset className="form-section">
            <legend>Shipment & Pallets</legend>

            <div className="form-group">
              <label htmlFor="shipment">Select Shipment *</label>
              <select
                id="shipment"
                name="shipment_id"
                value={formData.shipment_id}
                onChange={handleShipmentChange}
                required
              >
                <option value="">-- Select a shipment --</option>
                {shipments.map(shipment => (
                  <option key={shipment.packing_list_id} value={shipment.packing_list_id}>
                    {shipment.order_number || shipment.invoice_number || `PL-${shipment.packing_list_id}`}
                  </option>
                ))}
              </select>
            </div>

            {cargoItems.length > 0 && (
              <div className="pallet-selection">
                <label>Select Pallets to Deliver *</label>
                <div className="cargo-list">
                  {cargoItems.map(item => {
                    const cargoKey = `cargo-${item.cargo_id}`;
                    const selectedCount = (selectedPallets[cargoKey] || []).length;
                    const availablePallets = getAvailablePalletNumbers(item);
                    const totalPallets = item.pallet_numbers ? item.pallet_numbers.length : 0;
                    const inventory = item.inventory;
                    
                    return (
                      <div key={item.cargo_id} className="cargo-section">
                        <div className="cargo-header">
                          <h4>{item.material_description}</h4>
                          <div className="cargo-stock-info">
                            <span className="pallet-count">
                              {selectedCount} of {availablePallets.length} available selected
                            </span>
                            {inventory && (
                              <span className="qty-remaining">
                                {inventory.quantity_remaining} of {inventory.quantity_pieces} pcs in stock
                              </span>
                            )}
                          </div>
                        </div>

                        {item.pallet_numbers && item.pallet_numbers.length > 0 ? (
                          <div className="pallets-container">
                            {availablePallets.length > 0 && (
                              <div className="select-all">
                                <input
                                  type="checkbox"
                                  id={`select-all-${item.cargo_id}`}
                                  checked={selectedCount === availablePallets.length && availablePallets.length > 0}
                                  onChange={() => handleSelectAllPallets(item)}
                                />
                                <label htmlFor={`select-all-${item.cargo_id}`}>
                                  Select All Available ({availablePallets.length})
                                </label>
                              </div>
                            )}

                            <div className="pallets-grid">
                              {item.pallet_numbers.map((palletNumber, idx) => {
                                const palletInfo = getPalletInfo(item, palletNumber);
                                const status = palletInfo?.status || 'available';
                                const isAvailable = status === 'available';
                                const isSelected = (selectedPallets[cargoKey] || []).includes(palletNumber);

                                return (
                                  <div
                                    key={idx}
                                    className={`pallet-checkbox pallet-${status} ${!isAvailable ? 'pallet-unavailable' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      id={`pallet-${item.cargo_id}-${idx}`}
                                      checked={isSelected}
                                      disabled={!isAvailable}
                                      onChange={() => handlePalletToggle(item.cargo_id, palletNumber, item)}
                                    />
                                    <label htmlFor={`pallet-${item.cargo_id}-${idx}`}>
                                      <span className="pallet-label">{palletNumber}</span>
                                      <span className={`pallet-status-tag status-${status}`}>
                                        {status}
                                      </span>
                                      {palletInfo?.delivery_number && (
                                        <span className="pallet-delivery-ref">{palletInfo.delivery_number}</span>
                                      )}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="no-pallets">No pallet numbers assigned</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {formData.shipment_id && cargoItems.length === 0 && (
              <div className="no-items-message">
                No cargo items found for this shipment
              </div>
            )}
          </fieldset>
        </div>

        {/* RIGHT COLUMN: Delivery Details */}
        <div className="form-column">
          <fieldset className="form-section">
            <legend>Delivery Details</legend>

            <div className="form-group">
              <label htmlFor="customerName">Customer Name *</label>
              <input
                id="customerName"
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleFormChange}
                placeholder="e.g., ABC Industries"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="deliveryAddress">Delivery Address *</label>
              <textarea
                id="deliveryAddress"
                name="delivery_address"
                value={formData.delivery_address}
                onChange={handleFormChange}
                placeholder="Full delivery address"
                rows="3"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="deliveryCity">City</label>
                <input
                  id="deliveryCity"
                  type="text"
                  name="delivery_city"
                  value={formData.delivery_city}
                  onChange={handleFormChange}
                  placeholder="City"
                />
              </div>

              <div className="form-group">
                <label htmlFor="deliveryCountry">Country</label>
                <input
                  id="deliveryCountry"
                  type="text"
                  name="delivery_country"
                  value={formData.delivery_country}
                  onChange={handleFormChange}
                  placeholder="Country"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="truckModel">Truck Model</label>
              <input
                id="truckModel"
                type="text"
                name="truck_model"
                value={formData.truck_model}
                onChange={handleFormChange}
                placeholder="e.g., Volvo FH16"
              />
            </div>

            <div className="form-group">
              <label htmlFor="truckRegistration">Truck Registration</label>
              <input
                id="truckRegistration"
                type="text"
                name="truck_registration"
                value={formData.truck_registration}
                onChange={handleFormChange}
                placeholder="e.g., ABC-123-CD"
              />
            </div>

            <div className="form-group">
              <label htmlFor="driverName">Driver Name *</label>
              <input
                id="driverName"
                type="text"
                name="driver_name"
                value={formData.driver_name}
                onChange={handleFormChange}
                placeholder="e.g., John Smith"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="driverPhone">Driver Phone</label>
              <input
                id="driverPhone"
                type="text"
                name="driver_phone"
                value={formData.driver_phone}
                onChange={handleFormChange}
                placeholder="e.g., +1 234 567 8900"
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Any additional notes"
                rows="2"
              />
            </div>
          </fieldset>
        </div>

        <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
          {loading ? 'Creating Delivery Note...' : 'Create Delivery Note'}
        </button>
      </form>
    </div>
  );
}
